"use client";
import React from "react";
import { BlockUpdateFunction } from ".";
import { IBlocks, ITheme, IUserData } from "@/types";
import AdminPopover from "../../content/AdminPopover";
import { Button } from "@/components/ui/button";
import BlockToggle from "./tabs/toggle";
import PodbeanOptions from "./tabs/podbean/options";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import moment from "moment";
import LoadingSkeleton from "./LoadingSkeleton";
import PodbeanCustomization from "./tabs/podbean/customization";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    theme: ITheme;
    selectedPreviewDevice?: string;
    newBlock?: string | null;
}

export default function BlockPodbean({
    admin,
    user,
    block,
    update,
    style,
    theme,
    selectedPreviewDevice,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [rssData, setRssData] = React.useState<any>(null);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    React.useEffect(() => {
        // Fetch RSS feed data when the component mounts
        fetchRssData();
    }, []);

    const fetchRssData = async () => {
        try {
            const response = await fetch(blockUpdate.url);
            const data = await response.text();
            const parser = new DOMParser();
            const xml = parser.parseFromString(data, "application/xml");
            const title = xml.querySelector("title")?.textContent || "";
            const description =
                xml.querySelector("description")?.textContent || "";
            const urlElement = xml.querySelector("image > url");
            const image = urlElement ? urlElement?.textContent?.trim() : null;
            const items = xml.querySelectorAll("item");
            const item = {
                title: items[0].querySelector("title")?.textContent || "",
                link: items[0].querySelector("link")?.textContent || "",
                description:
                    items[0].querySelector("description")?.textContent || "",
                pubDate: items[0].querySelector("pubDate")?.textContent || "",
            };
            setRssData({
                title,
                description,
                image,
                item,
            });
        } catch (error) {
            setRssData(null);
            console.error("Error fetching RSS data:", error);
        }
    };
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "PodBean",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <PodbeanOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <PodbeanCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={[]}
                        user={user}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            fetchRssData();
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);

    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={`PodBean | ${rssData?.title || ""}`}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            <div className="w-full">
                {rssData ? (
                    <div
                        className={`space-y-3
                    ${theme?.button_shadow}
                    ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                    `}
                        style={style}
                    >
                        <Link
                            className={`w-full`}
                            href={rssData.item.link}
                            target="_blank"
                        >
                            <button
                                style={style}
                                id="podbean"
                                className={` {formatClassName(text)} relative 
                ${theme?.button_shadow}
                ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                w-full 
                px-1 
                py-3 text-xl
                shadow-md transition-all 
                duration-150 hover:scale-105
                `}
                            >
                                <div className={`flex justify-start gap-1`}>
                                    <div className="w-1/3">
                                        <img
                                            src={rssData.image}
                                            className={`h-auto ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}`}
                                            alt="custom icons"
                                            // style={{ width: 52, height: 52 }}
                                        />
                                    </div>
                                    <div className="flex w-2/3 flex-col gap-2 px-2">
                                        <span
                                            id="block-text"
                                            className={`w-full text-start font-bold`}
                                        >
                                            {rssData.title}
                                        </span>
                                        <Label
                                            className="line-clamp-3 text-start"
                                            style={{ color: style.color }}
                                        >
                                            {rssData.description}
                                        </Label>
                                        <div className="mt-auto">
                                            <Label
                                                className="text-start font-bold"
                                                style={{ color: style.color }}
                                            >
                                                Latest Episode:{" "}
                                                {rssData.item.title}
                                            </Label>
                                            <Label
                                                className="text-start"
                                                style={{ color: style.color }}
                                            >
                                                Uploaded:{" "}
                                                {moment(
                                                    rssData.item.pubDate
                                                ).format("MMM Do YYYY")}
                                            </Label>
                                        </div>
                                    </div>
                                </div>
                            </button>
                        </Link>
                    </div>
                ) : (
                    <LoadingSkeleton className="" />
                )}
            </div>
        </AdminPopover>
    );
}
